import {
  useId,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

type Variant = 'primary' | 'secondary' | 'danger' | 'link';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: Variant;
  /** Shows the button as busy and disables it. */
  readonly busy?: boolean;
}

export function Button({
  variant = 'primary',
  busy = false,
  className,
  type = 'button',
  disabled,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      type={type}
      disabled={disabled === true || busy}
      aria-busy={busy || undefined}
      className={['mrigl-button', `mrigl-button--${variant}`, className].filter(Boolean).join(' ')}
    >
      {children}
    </button>
  );
}

interface FieldChrome {
  readonly label: string;
  readonly hint?: string | undefined;
  readonly error?: string | undefined;
}

export type TextFieldProps = FieldChrome &
  InputHTMLAttributes<HTMLInputElement> & { readonly ltr?: boolean };

/** Labelled input with hint and error wired through aria-describedby / aria-invalid. */
export function TextField({
  label,
  hint,
  error,
  ltr = false,
  className,
  ...input
}: TextFieldProps) {
  const id = useId();
  const hintId = hint === undefined ? undefined : `${id}-hint`;
  const errorId = error === undefined ? undefined : `${id}-error`;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  return (
    <div className="mrigl-field">
      <label className="mrigl-field__label" htmlFor={id}>
        {label}
      </label>
      {hint === undefined ? null : (
        <span className="mrigl-field__hint" id={hintId}>
          {hint}
        </span>
      )}
      <input
        {...input}
        id={id}
        dir={ltr ? 'ltr' : input.dir}
        aria-invalid={error === undefined ? undefined : true}
        aria-describedby={describedBy}
        className={['mrigl-input', ltr ? 'mrigl-ltr' : undefined, className]
          .filter(Boolean)
          .join(' ')}
      />
      {error === undefined ? null : (
        <span className="mrigl-field__error" id={errorId}>
          {error}
        </span>
      )}
    </div>
  );
}

export type TextAreaFieldProps = FieldChrome & TextareaHTMLAttributes<HTMLTextAreaElement>;

/** Multi-line text with the same label/hint/error wiring as TextField. */
export function TextAreaField({ label, hint, error, className, ...textarea }: TextAreaFieldProps) {
  const id = useId();
  const hintId = hint === undefined ? undefined : `${id}-hint`;
  const errorId = error === undefined ? undefined : `${id}-error`;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  return (
    <div className="mrigl-field">
      <label className="mrigl-field__label" htmlFor={id}>
        {label}
      </label>
      {hint === undefined ? null : (
        <span className="mrigl-field__hint" id={hintId}>
          {hint}
        </span>
      )}
      <textarea
        rows={4}
        {...textarea}
        id={id}
        aria-invalid={error === undefined ? undefined : true}
        aria-describedby={describedBy}
        className={['mrigl-input', 'mrigl-textarea', className].filter(Boolean).join(' ')}
      />
      {error === undefined ? null : (
        <span className="mrigl-field__error" id={errorId}>
          {error}
        </span>
      )}
    </div>
  );
}

export interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  readonly label: string;
  readonly options: readonly {
    readonly value: string;
    readonly label: string;
    readonly lang?: string;
  }[];
}

export function SelectField({ label, options, className, ...select }: SelectFieldProps) {
  const id = useId();
  return (
    <div className="mrigl-field">
      <label className="mrigl-field__label" htmlFor={id}>
        {label}
      </label>
      <select {...select} id={id} className={['mrigl-select', className].filter(Boolean).join(' ')}>
        {options.map((option) => (
          <option key={option.value} value={option.value} lang={option.lang}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export interface AlertProps {
  readonly tone: 'error' | 'success';
  readonly children: ReactNode;
}

/** Errors interrupt (role=alert); confirmations are polite (role=status). */
export function Alert({ tone, children }: AlertProps) {
  return (
    <div
      className={`mrigl-alert mrigl-alert--${tone}`}
      role={tone === 'error' ? 'alert' : 'status'}
    >
      {children}
    </div>
  );
}

export function Card({
  children,
  labelledBy,
}: {
  readonly children: ReactNode;
  readonly labelledBy?: string;
}) {
  return (
    <section className="mrigl-card mrigl-stack" aria-labelledby={labelledBy}>
      {children}
    </section>
  );
}

export interface CheckboxGroupProps {
  readonly legend: string;
  readonly options: readonly {
    readonly value: string;
    readonly label: string;
    readonly lang?: string;
  }[];
  readonly values: readonly string[];
  readonly onChange: (values: string[]) => void;
  readonly error?: string;
}

/** Grouped checkboxes in a fieldset/legend (WCAG 1.3.1), error described on the group. */
export function CheckboxGroup({ legend, options, values, onChange, error }: CheckboxGroupProps) {
  const id = useId();
  const errorId = error === undefined ? undefined : `${id}-error`;
  return (
    <fieldset className="mrigl-fieldset" aria-describedby={errorId}>
      <legend className="mrigl-field__label">{legend}</legend>
      {options.map((option) => (
        <label key={option.value} className="mrigl-checkbox">
          <input
            type="checkbox"
            checked={values.includes(option.value)}
            onChange={(event) => {
              onChange(
                event.target.checked
                  ? [...values, option.value]
                  : values.filter((value) => value !== option.value),
              );
            }}
          />
          <span lang={option.lang}>{option.label}</span>
        </label>
      ))}
      {error === undefined ? null : (
        <span className="mrigl-field__error" id={errorId}>
          {error}
        </span>
      )}
    </fieldset>
  );
}
